import React, { useState, useEffect } from "react";
import {
  FaTimes,
  FaEye,
  FaEyeSlash,
  FaStore,
  FaEnvelope,
  FaLock,
} from "react-icons/fa";
import { useDispatch } from "react-redux";
import axios from "axios";
import tw from "tailwind-styled-components";
import Config from "../Config";
import { Save_User } from "../Redux/actions";
// import ForgotPasswordPage from '../Pages/ForgotPassword';
import { InputGroup } from "./Styles/InputStyles";
import { useNavigate } from "react-router-dom";

const EmailLoginModal = ({
  isOpen,
  onClose,
  onSwitchToSignup,
  onSwitchToEmailLogin,
  onLoginSuccess,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({ email: "", password: "" });
      setErrors({});
      setShowPassword(false);
      setIsLoading(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Email validation
  const validateEmail = (email) => {
    const emailFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailFormat.test(email);
  };

  const validateField = (name, value) => {
    // Handle undefined/null values
    if (value === undefined || value === null) {
      value = "";
    }

    switch (
      name // Field-specific validation
    ) {
      case "email":
        if (!value.trim()) return "Please enter your email address";
        if (!validateEmail(value)) return "Please enter a valid email address";
        return "";
      case "password":
        if (!value) return "Please enter your password";
        if (value.length < 8) return "Password must be at least 8 characters";
        return "";
      default:
        return "";
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target; // validate fields on change in input value
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear general error when user starts typing
    if (errors.general) {
      setErrors((prev) => ({
        ...prev,
        general: "",
      }));
    }

    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]); // Validate each field
      if (error) newErrors[key] = error;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      // API call to login with email and password
      const response = await axios.post(`${Config.apiUrl}/auth/login`, {
        email: formData.email.trim(),
        password: formData.password,
      });

      if (response.data && response.data.success) {
        // console.log("user login response data >> ", response.data.data.user);
        // Save user data to Redux store
        const userData = {
          ...response.data.data.user,
          token: response.data.data.token,
        };

        dispatch(Save_User(userData));

        if (userData.role === Config.userType.SELLER) {
          navigate("/admin");
          // navigate to seller dashboard if the user is seller
        } else {
          navigate("/"); // navigate to home
        }

        onLoginSuccess && onLoginSuccess(userData);
        onClose();
      } else {
        setErrors({
          general:
            response.data?.message ||
            "Login failed. Please check your credentials.",
        });
      }
    } catch (error) {
      console.error("Login failed:", error);
      setErrors({
        general:
          error.response?.data?.message ||
          "Login failed. Please check your credentials and try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = // check if form is valid for enabling login button
    formData.email.trim() &&
    formData.password &&
    validateEmail(formData.email) &&
    Object.keys(errors).every((key) => !errors[key]);

  return (
    <ModalOverlay>
      <ModalContainer>
        <CloseButton // Close button
          onClick={onClose}
          disabled={isLoading}
        >
          <FaTimes className="w-4 h-4" />
        </CloseButton>

        <ModalContent>
          <Title>Login with Password</Title>

          {errors.general && <ErrorMessage>{errors.general}</ErrorMessage>}

          <StyledForm onSubmit={handleLogin}>
            {/* Email Input */}
            <InputGroup>
              <div className="relative">
                <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  // autoComplete="email"
                  autoComplete="username"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  $hasError={!!errors.email}
                  className="pl-10"
                />
              </div>
              {errors.email && <ErrorText>{errors.email}</ErrorText>}
            </InputGroup>

            {/* Password Input */}
            <InputGroup>
              <PasswordContainer>
                <div className="relative w-full">
                  <FaLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Password"
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    $hasError={!!errors.password}
                    className="pl-10"
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </PasswordToggle>
                </div>
              </PasswordContainer>
              {errors.password && <ErrorText>{errors.password}</ErrorText>}
            </InputGroup>

            {/* Login Button */}
            <LoginButton
              type="submit"
              disabled={!isFormValid || isLoading}
              $isValid={isFormValid && !isLoading}
            >
              {isLoading ? "Logging in..." : "Login"}
            </LoginButton>
          </StyledForm>

          {/* Divider */}
          <Divider>
            <DividerLine></DividerLine>
            <DividerText>or</DividerText>
            <DividerLine></DividerLine>
          </Divider>
          <SwitchText>
            {/* Forgot your password?{" "} */}
          <SwitchLink onClick={onSwitchToEmailLogin} disabled={isLoading}>
            Login with OTP</SwitchLink> 
          <div className="text-kuality-blue select-none">|</div>
          {/* </SwitchText> */}

          {/* Email OTP Login */}
          {/* <OTPLoginButton onClick={onSwitchToEmailLogin} disabled={isLoading}>
            <span>Login with OTP</span>
          </OTPLoginButton> */}

          {/* Switch to Signup */}
          {/* <SwitchText> */}
            {/* New to our website?{" "} */}
            <SwitchLink onClick={onSwitchToSignup}>Create account</SwitchLink>
          </SwitchText>

          <SellerLink
            href={Config.sellerDomain}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaStore className="text-lg" />
            <span>Become a Supplier</span>
          </SellerLink>
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};

// Styled Components
const ModalOverlay = tw.div`
  fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-md 
`;

const ModalContainer = tw.div`
  bg-white rounded-lg max-w-sm w-full max-h-[90vh] overflow-y-auto relative
`;

const CloseButton = tw.button`
  absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10 transition-colors
`;

const ModalContent = tw.div`
  p-8 pt-12
`;

const Title = tw.h2`
  text-2xl font-semibold text-kuality-blue mb-6
`;

const ErrorMessage = tw.div`
  bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4 text-sm
`;

const StyledForm = tw.form`
  space-y-4
`;

const PasswordContainer = tw.div`
  relative flex items-center
`;

const Input = tw.input`
  w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-kuality-blue text-gray-700 placeholder-gray-400 disabled:bg-gray-50 disabled:text-gray-500 transition-colors
  ${(props) =>
    props.$hasError ? "border-red-500 focus:border-red-600 pr-10" : ""}
  ${(props) =>
    props.type === "password" || (props.$hasError && props.name === "password")
      ? "pr-10"
      : ""}
`;

const PasswordToggle = tw.button`
  absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors
`;

const ErrorText = tw.span`
  text-red-500 text-xs mt-1 block
`;

const LoginButton = tw.button`
  w-full py-3 rounded-lg font-medium transition-colors duration-200 mb-4 text-white
  ${(props) =>
    props.$isValid
      ? "bg-kuality-blue hover:bg-kuality-dark cursor-pointer"
      : "bg-gray-300 text-gray-500 cursor-not-allowed"}
  ${(props) => (props.disabled ? "opacity-50 cursor-not-allowed" : "")}
`;

const Divider = tw.div`
  flex items-center my-1
`;

const DividerLine = tw.div`
  flex-1 h-px bg-gray-300
`;

const DividerText = tw.span`
  px-4 text-gray-500 text-sm
`;

const OTPLoginButton = tw.button`
  w-full flex items-center justify-center space-x-2 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed py-3 rounded-lg font-medium transition-colors duration-200 mb-3
`;

// const SwitchText = tw.p`
//   text-center text-gray-600 text-sm
// `;

const SwitchText = tw.p`
  flex items-center justify-center space-x-1 text-sm
`;

const SwitchLink = tw.span`
  text-kuality-blue hover:text-kuality-dark cursor-pointer font-medium transition-colors
`;

const SellerLink = tw.a`
  w-full flex items-center justify-center space-x-2 border border-kuality-blue text-kuality-blue hover:shadow-md py-3 rounded-lg font-medium transition-colors duration-200 mt-2 cursor-pointer no-underline
`;

export default EmailLoginModal;
